import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserTie, FaSearch, FaCheckCircle, FaTrash } from 'react-icons/fa';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as adminService from '../services/adminService';

const EditorialManagement = () => {
    const [editorialMembers, setEditorialMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [selectedMember, setSelectedMember] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [actionType, setActionType] = useState(''); // Added to distinguish actions
    const [viewFilter, setViewFilter] = useState('all'); // 'all', 'members', 'candidates', 'unauthorized'
    const [unauthorizedMemberIds, setUnauthorizedMemberIds] = useState(new Set());
    const navigate = useNavigate();

    // Check if user is admin
    useEffect(() => {
        const user = authService.getCurrentUser();
        if (!user) {
            navigate('/login');
            return;
        }
        if (user.role !== 'admin') {
            navigate('/');
            return;
        }

        loadEditorialBoard();
    }, [navigate]);

    const loadEditorialBoard = async () => {
        setLoading(true);

        try {
            // Fetch data from endpoints
            const [professorsRes, editorialRes, reviewersRes, unauthorizedRes] = await Promise.all([
                adminService.getProfessors(),
                adminService.getEditorialAssignments(),
                adminService.getReviewers(),
                adminService.getUnauthorizedEditorialBoard()
            ]);

            // Normalize responses
            const allProfessors = professorsRes.data || professorsRes;
            const editorialBoard = editorialRes.data || editorialRes;
            const reviewersList = reviewersRes.data || reviewersRes;
            const unauthorizedList = unauthorizedRes.data || unauthorizedRes;

            const editorialIds = new Set((Array.isArray(editorialBoard) ? editorialBoard : []).map(item => item.professor_id || item.user_id || item.id));
            const reviewerIds = new Set((Array.isArray(reviewersList) ? reviewersList : []).map(item => item.professor_id || item.user_id || item.id));
            const unauthorizedIds = new Set((Array.isArray(unauthorizedList) ? unauthorizedList : []).map(item => item.professor_id || item.user_id || item.id));

            setUnauthorizedMemberIds(unauthorizedIds);

            // Merge data
            const processorsWithRoles = (Array.isArray(allProfessors) ? allProfessors : []).map(prof => {
                const userData = prof.user || {};
                const id = prof.user_id || prof.id;
                return {
                    ...prof,
                    name: userData.full_name || prof.full_name || userData.name || prof.name || 'Unknown',
                    email: userData.email || prof.email || 'N/A',
                    phone: userData.phone_number || prof.phone_number || prof.phone || 'N/A',
                    id: id,
                    editorialBoard: prof.is_editorial_board !== undefined ? prof.is_editorial_board : editorialIds.has(id),
                    reviewer: prof.is_reviewer !== undefined ? prof.is_reviewer : reviewerIds.has(id),
                    isUnauthorized: unauthorizedIds.has(id)
                };
            });

            // Keep all for candidate separation
            setEditorialMembers(processorsWithRoles);
        } catch (error) {
            console.error('Error loading editorial board:', error);
            showToast('Failed to load editorial data', 'error');
            setEditorialMembers([]);
        } finally {
            setLoading(false);
        }
    };

    const getFilteredMembers = () => {
        let filtered = [...editorialMembers];

        if (searchTerm && searchTerm.trim() !== '') {
            const search = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(mem => {
                const name = (mem.name || '').toLowerCase();
                const email = (mem.email || '').toLowerCase();
                const phone = (mem.phone || mem.phone_number || '').toLowerCase();
                return name.includes(search) || email.includes(search) || phone.includes(search);
            });
        }

        return filtered;
    };

    const filteredMembers = getFilteredMembers();
    const assignedMembers = filteredMembers.filter(m => m.editorialBoard === true);
    let candidateMembers = filteredMembers.filter(m => m.editorialBoard === false);

    // Prepare lists based on viewFilter
    const showMembers = viewFilter === 'all' || viewFilter === 'members';
    const showCandidates = viewFilter === 'all' || viewFilter === 'candidates' || viewFilter === 'unauthorized';

    // Further filter candidates if 'unauthorized' is selected
    const displayCandidates = viewFilter === 'unauthorized'
        ? candidateMembers.filter(m => m.isUnauthorized === true)
        : candidateMembers;

    const displayMembers = showMembers ? assignedMembers : [];
    const finalCandidateList = showCandidates ? displayCandidates : [];

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const handleAction = (member, type) => {
        setSelectedMember(member);
        setActionType(type);
        setShowConfirmModal(true);
    };

    const confirmAction = async () => {
        if (!selectedMember) return;

        try {
            if (actionType === 'editorial') {
                const isCurrentlyAssigned = selectedMember.editorialBoard;
                if (isCurrentlyAssigned) {
                    await adminService.removeFromEditorialBoard(selectedMember.id);
                    showToast(`${selectedMember.name} removed from Editorial Board`, 'success');
                } else {
                    await adminService.assignToEditorialBoard({ professor_id: selectedMember.id });
                    showToast(`${selectedMember.name} added to Editorial Board`, 'success');
                }
            } else if (actionType === 'reviewer') {
                const isCurrentlyAssigned = selectedMember.reviewer;
                if (isCurrentlyAssigned) {
                    await adminService.removeFromReviewers(selectedMember.id);
                    showToast(`${selectedMember.name} removed from Reviewers`, 'success');
                } else {
                    await adminService.assignAsReviewer({ professor_id: selectedMember.id });
                    showToast(`${selectedMember.name} assigned as Reviewer`, 'success');
                }
            } else if (actionType === 'reject') {
                await adminService.rejectUnauthorizedEditorialBoard(selectedMember.id);
                showToast(`${selectedMember.name} application rejected`, 'success');
            }

            await loadEditorialBoard();

        } catch (error) {
            console.error('Error updating role:', error);
            showToast(error.message || 'Error updating role', 'error');
        } finally {
            setShowConfirmModal(false);
            setSelectedMember(null);
            setActionType('');
        }
    };

    const darkGradient = {
        background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading editorial board...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            {/* Confirmation Modal */}
            {showConfirmModal && selectedMember && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaUserTie className="w-8 h-8 text-blue-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">Confirm Action</h3>
                            <p className="text-gray-600">
                                {actionType === 'editorial'
                                    ? (selectedMember.editorialBoard
                                        ? `Remove ${selectedMember.name} from Editorial Board?`
                                        : `Add ${selectedMember.name} to Editorial Board?`)
                                    : (actionType === 'reject'
                                        ? `Reject ${selectedMember.name}'s application? This will remove them from editorial management.`
                                        : (selectedMember.reviewer
                                            ? `Remove ${selectedMember.name} from Reviewers?`
                                            : `Assign ${selectedMember.name} as Reviewer?`))
                                }
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => { setShowConfirmModal(false); setSelectedMember(null); }}
                                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAction}
                                className={`flex-1 px-4 py-3 text-white rounded-lg font-semibold hover:shadow-lg transition-all ${actionType === 'editorial' && selectedMember.editorialBoard ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w mx-auto">
                {/* Header */}
                <div className="rounded-2xl shadow-lg p-6 mb-6" style={darkGradient}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                <FaUserTie className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-white">Editorial Management</h1>
                                <p className="text-blue-100 mt-1">Manage editorial board members and candidates</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-blue-200">Total Professors</p>
                            <p className="text-3xl font-bold text-white">{editorialMembers.length}</p>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm text-blue-600 font-semibold uppercase">Board Members</p>
                                <p className="text-2xl font-bold text-blue-900">{assignedMembers.length}</p>
                            </div>
                            <FaCheckCircle className="text-3xl text-blue-500" />
                        </div>
                        <div className="p-4 bg-purple-50 rounded-xl border border-purple-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm text-purple-600 font-semibold uppercase">Candidates</p>
                                <p className="text-2xl font-bold text-purple-900">{candidateMembers.length}</p>
                            </div>
                            <FaUserTie className="text-3xl text-purple-500" />
                        </div>
                    </div>
                </div>

                {/* Search */}
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name, email, or phone..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            />
                        </div>
                        <div className="md:w-64">
                            <select
                                value={viewFilter}
                                onChange={(e) => setViewFilter(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white text-gray-700 font-medium"
                            >
                                <option value="all">View Everything</option>
                                <option value="members">Board Members</option>
                                <option value="candidates">All Candidates</option>
                                <option value="unauthorized">Unauthorized Only</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* List Sections */}
                <div className="space-y-8">
                    {/* Assigned Section */}
                    {displayMembers.length > 0 && (
                        <div>
                            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <FaCheckCircle className="text-blue-600" />
                                Editorial Board Members
                            </h2>
                            <div className="space-y-4">
                                {displayMembers.map((member, index) => (
                                    <div key={`assigned-${member.email}-${index}`} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                                        <div className="p-6">
                                            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                                                <div className="flex-1">
                                                    <div className="flex items-start gap-4">
                                                        <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0" style={darkGradient}>
                                                            <span className="text-2xl font-bold text-white">
                                                                {member.name.charAt(0).toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <div className="flex-1">
                                                            <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                                                            <p className="text-gray-600 text-sm mb-2">{member.email}</p>
                                                            {member.phone && <p className="text-gray-500 text-sm">📞 {member.phone}</p>}
                                                            <div className="mt-2 flex gap-2">
                                                                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">Editorial Board</span>
                                                                {member.reviewer && (
                                                                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">Reviewer</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col sm:flex-row gap-3 lg:flex-col lg:w-64">
                                                    <button
                                                        onClick={() => handleAction(member, 'editorial')}
                                                        className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold bg-red-50 text-red-600 border-2 border-red-200 hover:bg-red-100 transition-all duration-300"
                                                    >
                                                        <FaTrash className="w-4 h-4" />
                                                        <span>Revoke Rights</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Candidates Section */}
                    {finalCandidateList.length > 0 && (
                        <div>
                            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <FaUserTie className="text-purple-600" />
                                {viewFilter === 'unauthorized' ? 'Unauthorized Candidates' : 'Editorial Board Candidates'}
                            </h2>
                            <div className="space-y-4">
                                {finalCandidateList.map((member, index) => (
                                    <div key={`candidate-${member.email}-${index}`} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-purple-400">
                                        <div className="p-6">
                                            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                                                <div className="flex-1">
                                                    <div className="flex items-start gap-4">
                                                        <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-2xl shadow-md">
                                                            {member.name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2">
                                                                <h3 className="text-xl font-bold text-gray-900">{member.name}</h3>
                                                                {member.isUnauthorized ? (
                                                                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold uppercase tracking-wider animate-pulse">Unauthorized</span>
                                                                ) : (
                                                                    <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-[10px] font-bold uppercase tracking-wider">Candidate</span>
                                                                )}
                                                            </div>
                                                            <p className="text-gray-600 text-sm mb-2">{member.email}</p>
                                                            {member.phone && <p className="text-gray-500 text-sm">📞 {member.phone}</p>}
                                                            <div className="mt-2 flex gap-2">
                                                                <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">Professor</span>
                                                                {member.reviewer && (
                                                                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">Reviewer</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col sm:flex-row gap-3 lg:flex-col lg:w-64">
                                                    <button
                                                        onClick={() => handleAction(member, 'editorial')}
                                                        className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:shadow-lg transform hover:scale-[1.02] transition-all duration-300"
                                                    >
                                                        <FaCheckCircle className="w-5 h-5" />
                                                        <span>Accept Member</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleAction(member, 'reject')}
                                                        className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold bg-white text-red-600 border-2 border-red-200 hover:bg-red-50 transition-all duration-300"
                                                    >
                                                        <FaTrash className="w-5 h-5" />
                                                        <span>Reject</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {filteredMembers.length === 0 && (
                        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border-2 border-dashed border-gray-200">
                            <FaUserTie className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Profiles Found</h3>
                            <p className="text-gray-600">Try adjusting your search criteria or clear the filters.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditorialManagement;